import React, { useEffect, useState } from 'react';
import { Menu } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PhotoIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import IMAGES from '../../assets/images';
import { strings } from '../../localization/strings';
import { parameters } from '../../constants/parameters';
import { useAuth } from '../../hooks/use-auth';
import { usePermissions } from '../../hooks/use-permissions';

interface SubMenuItem {
  title: string;
  href: string;
  roles?: string[];
  permissions?: string[];
}

interface MenuGroup {
  id: string;
  title: string;
  icon: typeof PhotoIcon;
  defaultHref?: string;
  items: SubMenuItem[];
}

const groups: MenuGroup[] = [
  // Enable once routes exist; add NewspaperIcon and DocumentTextIcon imports.
  // {
  //   id: 'news', title: strings.navigation.news, icon: NewspaperIcon, defaultHref: '/news/published',
  //   items: [
  //     { title: strings.navigation.preparing, href: '/news/preparing' },
  //     { title: strings.navigation.scheduled, href: '/news/scheduled' },
  //     { title: strings.navigation.published, href: '/news/published' },
  //     { title: strings.navigation.agencies, href: '/news/agencies' },
  //     { title: strings.navigation.publicNews, href: '/news/public' },
  //     { title: strings.navigation.trash, href: '/news/trash' },
  //   ],
  // },
  // {
  //   id: 'pressreleases', title: strings.navigation.pressreleases, icon: DocumentTextIcon, defaultHref: '/pressreleases/published',
  //   items: [
  //     { title: strings.navigation.preparing, href: '/pressreleases/preparing' },
  //     { title: strings.navigation.scheduled, href: '/pressreleases/scheduled' },
  //     { title: strings.navigation.published, href: '/pressreleases/published' },
  //     { title: strings.navigation.trash, href: '/pressreleases/trash' },
  //   ],
  // },
  {
    id: 'media',
    title: strings.navigation.media,
    icon: PhotoIcon,
    items: [
      { title: strings.navigation.localPhotos, href: '/photos/local', permissions: ['crud photo'] },
      { title: strings.navigation.foreignPhotos, href: '/photos/foreign', permissions: ['crud photo'] },
      { title: strings.navigation.photoAlbums, href: '/albums', permissions: ['crud photo'] },
      { title: strings.navigation.video, href: '/videos', permissions: ['crud photo'] },
    ],
  },
  {
    id: 'clients',
    title: strings.navigation.clients,
    icon: UsersIcon,
    items: [
      { title: strings.navigation.companies, href: '/companies', permissions: ['crud companies'] },
      { title: strings.navigation.users, href: '/users', permissions: ['crud users'] },
      { title: strings.navigation.easyToRead, href: '/easy-2-read/generator', permissions: ['crud easy to read'] },
    ],
  },
  {
    id: 'reports',
    title: strings.navigation.reports.title,
    icon: ChartBarIcon,
    items: [
      { title: strings.navigation.reports.content, href: '/reports/user-content', permissions: ['crud reports'] },
      { title: strings.navigation.reports.usage, href: '/reports/usage', permissions: ['crud reports usage'] },
      {
        title: strings.navigation.reports.pressreleaseStats,
        href: '/reports/pressrelease-stats',
        permissions: ['crud reports'],
      },
      { title: strings.navigation.reports.logs, href: '/reports/logs', permissions: ['view activity log'] },
    ],
  },
  {
    id: 'admin',
    title: strings.navigation.admin,
    icon: Cog6ToothIcon,
    items: [
      { title: strings.navigation.badges, href: '/badges', roles: ['contentmanager', 'admin'] },
      { title: strings.navigation.translations, href: '/translations', roles: ['contentmanager', 'admin'] },
      { title: strings.navigation.employees, href: '/employees', roles: ['admin'] },
      { title: strings.categories.title, href: '/categories', roles: ['contentmanager', 'admin'] },
    ],
  },
];

function SidebarComponent() {
  const { user, signout } = useAuth();
  const { hasAnyPermission, hasAnyRole } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => (!item.permissions || hasAnyPermission(item.permissions)) && (!item.roles || hasAnyRole(item.roles)),
      ),
    }))
    .filter((group) => group.items.length > 0);
  const activeGroup =
    visibleGroups.find((group) =>
      group.items.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`)),
    ) || visibleGroups[0];
  const previewGroup = visibleGroups.find((group) => group.id === previewId && group.id !== activeGroup?.id);

  useEffect(() => {
    setPreviewId(null);
  }, [pathname]);

  const renderSection = (group: MenuGroup, preview = false) => (
    <nav
      aria-label={`${strings.navigation.section}: ${group.title}`}
      className={`flex h-full flex-col bg-white ${preview ? 'absolute inset-0 z-10 shadow-lg' : ''} ${
        !preview && previewGroup ? 'invisible' : ''
      }`}
    >
      <div className="flex h-[60px] shrink-0 items-center border-b border-gray-100 px-5">
        <img className="h-[21px] w-auto" src={IMAGES.LOGOTYPES.PRIMARY} alt="ELTA" />
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-5">
        <ul className="space-y-1">
          {group.items.map((item) => (
            <li key={item.href}>
              <NavLink
                to={item.href}
                onClick={() => setPreviewId(null)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2.5 text-sm leading-5 no-underline transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${
                    isActive
                      ? 'bg-primary/10 font-medium text-primary'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                  }`
                }
              >
                {item.title}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
      <a
        className="mx-3 mb-4 flex items-center gap-2 rounded-md px-2 py-2 text-xs text-gray-500 no-underline hover:bg-gray-50 hover:text-primary"
        href={`${parameters.websiteUrl}/lt/backend`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {strings.navigation.returnToMainAdmin}
      </a>
    </nav>
  );

  return (
    <div
      role="presentation"
      className="flex min-h-0 w-full flex-1 border-r border-gray-200"
      onMouseLeave={() => setPreviewId(null)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPreviewId(null);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setPreviewId(null);
      }}
    >
      <div className="flex w-[72px] shrink-0 flex-col bg-[#122d63] text-white">
        <nav aria-label={strings.navigation.groups} className="flex-1 overflow-y-auto px-1.5 pt-3">
          <ul className="space-y-2">
            {visibleGroups.map((group) => {
              const Icon = group.icon;

              return (
                <li key={group.id} className={group.id === 'admin' ? 'border-t border-white/15 pt-3' : ''}>
                  <button
                    type="button"
                    aria-current={activeGroup?.id === group.id ? 'true' : undefined}
                    onMouseEnter={() => setPreviewId(group.id)}
                    onFocus={() => setPreviewId(group.id)}
                    onClick={() => {
                      setPreviewId(null);
                      navigate(
                        group.items.find((item) => item.href === group.defaultHref)?.href || group.items[0].href,
                      );
                    }}
                    className={`flex w-full flex-col items-center gap-1.5 rounded-lg px-1 py-3 text-[11px] font-medium leading-tight transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${
                      activeGroup?.id === group.id
                        ? 'bg-white/10 text-white'
                        : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                    <span>{group.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <Menu as="div" className="relative flex shrink-0 justify-center border-t border-white/15 py-4">
          <Menu.Button
            aria-label={strings.navigation.userMenu}
            title={user?.email}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          >
            {user?.email?.substring(0, 1).toUpperCase()}
          </Menu.Button>
          <Menu.Items className="absolute bottom-16 left-3 z-20 w-60 rounded-md bg-white p-2 text-gray-900 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
            <div className="truncate border-b border-gray-100 px-3 py-2 text-xs text-gray-500">{user?.email}</div>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => {
                    signout();
                    navigate('/');
                  }}
                  className={`mt-1 block w-full rounded px-3 py-2 text-left text-sm ${active ? 'bg-gray-50' : ''}`}
                >
                  {strings.navigation.signOut}
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Menu>
      </div>
      <div className="relative min-w-0 flex-1">
        {activeGroup && renderSection(activeGroup)}
        {previewGroup && renderSection(previewGroup, true)}
      </div>
    </div>
  );
}

export default SidebarComponent;
